import { defineComponent as _defineComponent } from 'vue'
import {
  NavBar,
  ContextColorProvider,
  LoaderIndicator,
  ButtonContainer,
  ButtonPrimary,
  HelperViewStatus
} from '@warrenbrasil/nebraska-web';
import { Hero, HeroContainer } from '@/modules/common/components/Hero';
import { Header } from './components/Header';
import { BackButton } from '@/modules/common/components/buttons';
import { computed, onMounted, ref } from 'vue';
import { ProfitSubscriptionContent } from './components/ProfitSubscriptionContent';
import { ProfitPreviewContent } from './components/ProfitPreviewContent';
import { ModalAcceptTerms } from './components/ModalAcceptTerms';
import { ProfitReview } from './components/ProfitReview';
import { VerifyPassword } from './components/VerifyPassword';
import {
  getProfitOptions,
  getProfitPreview,
  signProfitOption,
  verifyPassword
} from '@/modules/trade/services/profit';
import type {
  ProfitOptions,
  ProfitPreview
} from '@/modules/trade/services/profit';
import { useRoute, useRouter } from 'vue-router/composables';
import { getExternalAssetPath } from '@/modules/common/helpers/assets-resolver';
import { signature } from '@/modules/trade/services/terms';
import type { SignatureTerms } from '@/modules/trade/services/terms';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { isBFFError } from '@/modules/common/helpers/bff';

enum ProfitStep {
  TERMS = 'terms',
  REVIEW = 'review',
  PASSWORD = 'password'
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfitSubscriptionView',
  setup(__props) {

const isLoading = ref(false);
const hasError = ref(false);
const profitPreview = ref<ProfitPreview>();
const profitOption = ref<ProfitOptions>();
const profitOptionDetail = ref<ProfitOptions['profits']['options'][0] | null>(
  null
);

const step = ref<ProfitStep | null>();
const signatureTerms = ref<SignatureTerms | null>();
const isLoadingSignature = ref(false);
const isLoadingSubscription = ref(false);
const isPasswordValid = ref(true);
const isCheckingPassword = ref(false);

const route = useRoute();
const router = useRouter();

const profitHeroBg = getExternalAssetPath('hero/profit/desktop.jpg');

function handleGoBackClick() {
  router.back();
}

onMounted(() => {
  getProfitInfo();
});

const hasProfitPreviewButton = computed(() => {
  return profitPreview.value?.button?.active;
});

const hasProfitOptionButton = computed(() => {
  return profitOption.value?.button;
});

const isProfitPreviewButtonBuy = computed(() => {
  return profitPreview.value?.button?.action === 'buy';
});

const getBuyButtonLabel = computed(() => {
  if (hasProfitPreviewButton && isProfitPreviewButtonBuy)
    return profitPreview.value?.button?.text;
  return 'Assinar Profit';
});

const getAccessButtonLabel = computed(() => {
  return profitOption.value?.button?.text || 'Acessar Profit';
});

async function getProfitInfo() {
  isLoading.value = true;
  hasError.value = false;

  try {
    if (route.meta?.isPreview) {
      const profitValue = Number(route.params.profit);

      profitOptionDetail.value = (
        await getProfitOptions()
      ).profits.options.filter(option => option.value === profitValue)[0];

      profitPreview.value = await getProfitPreview(profitValue);
      profitOption.value = await getProfitOptions();
    } else {
      profitOption.value = await getProfitOptions();
    }
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

const getCurrentInfo = computed(() => {
  return route.meta!.isPreview ? profitPreview.value : profitOption.value;
});

function setStep(targetStep: ProfitStep | null) {
  step.value = targetStep;
}

async function acceptTerms() {
  isLoadingSignature.value = true;

  try {
    const { subscriptionPlan, value } = profitOptionDetail.value!;
    signatureTerms.value = await signature(`${subscriptionPlan}-${value}`);
    setStep(ProfitStep.REVIEW);
  } catch (e) {
    getErrorMessage(e);
    setStep(null);
  } finally {
    isLoadingSignature.value = false;
  }
}

async function checkPassword(inputPassword: string) {
  isCheckingPassword.value = true;
  try {
    isPasswordValid.value = await verifyPassword(inputPassword);

    if (isPasswordValid.value) subscribePlan();
  } catch (e) {
    getErrorMessage(e);
  } finally {
    isCheckingPassword.value = false;
  }
}

async function subscribePlan() {
  isLoadingSubscription.value = true;

  try {
    const { subscriptionPlan, value } = profitOptionDetail.value!;

    const subscription = await signProfitOption({
      product: value,
      subscriptionPlan,
      acceptanceTerm: signatureTerms.value?.term ?? ''
    });

    notify({
      group: 'wrn-notifications',
      text: subscription.message,
      type: NotificationTypes.success
    });

    router.push({ name: 'profitSubscription' });
  } catch (e) {
    getErrorMessage(e);
  } finally {
    isLoadingSubscription.value = false;
    setStep(null);
  }
}

function getErrorMessage(e: unknown) {
  let errorMessage = 'Houve um problema ao assinar o Profit.';
  if (isBFFError(e)) {
    errorMessage = e.error.message.formatted;
  }
  notify({
    group: 'wrn-notifications',
    text: errorMessage,
    type: NotificationTypes.error
  });
}

function onBackButtonClick() {
  if (route.meta?.isPreview) {
    router.push({ name: 'profitSubscription' });
  } else {
    router.push({ name: 'trade' });
  }
}

return { __sfc: true,isLoading, hasError, profitPreview, profitOption, profitOptionDetail, ProfitStep, step, signatureTerms, isLoadingSignature, isLoadingSubscription, isPasswordValid, isCheckingPassword, route, router, profitHeroBg, handleGoBackClick, hasProfitPreviewButton, hasProfitOptionButton, isProfitPreviewButtonBuy, getBuyButtonLabel, getAccessButtonLabel, getProfitInfo, getCurrentInfo, setStep, acceptTerms, checkPassword, subscribePlan, getErrorMessage, onBackButtonClick, NavBar, ContextColorProvider, LoaderIndicator, ButtonContainer, ButtonPrimary, HelperViewStatus, Hero, HeroContainer, Header, BackButton, ProfitSubscriptionContent, ProfitPreviewContent, ModalAcceptTerms, ProfitReview, VerifyPassword }
}

})